<template>
  <div>
    <div class="cursor-pointer">
      <component
        :is="rootTag"
        v-bind="rootAttrs"
        :data-testid="`sidebar-nav-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`"
        class="inline-flex flex-1 items-center font-medium w-full transition-colors duration-150 radius-base text-body-base sidebar-nav-item"
        :class="{
          'px-xs py-[6px]': 'action' in item && item.action,
          'p-xs': !('action' in item && item.action),
          'is-active': isExplicitlyActive,
        }"
      >
        <div class="flex-shrink-0 flex items-center justify-center" aria-hidden="true">
          <div v-if="item.icon" class="w-m h-m flex">
            <MeridiaIcon
              v-if="
                [
                  'material-icons',
                  'material-icons-outlined',
                  'material-symbols',
                  'material-symbols-outlined',
                ].includes(item.icon.type)
              "
              class="icon-s meridia-icon"
              :name="item.icon.value as string"
              :type="item.icon.type as any"
            />
            <div v-else-if="item.icon.type === 'component'">
              <component
                :is="item.icon.value"
                class="w-m h-m component-icon fill-neutral-text-subtitle"
              />
            </div>
          </div>
          <div v-else>
            <span class="inline-flex items-center justify-center w-m h-m text-body-base">
              {{ item.name[0] }}
            </span>
          </div>
        </div>

        <!-- Text: Motion handles width collapse + opacity fade -->
        <Motion.div
          :variants="textVariants"
          class="overflow-hidden min-w-0"
          :aria-hidden="!sidebar.open"
        >
          <div class="inline-flex w-full items-center whitespace-nowrap gap-xs"
            :style="{
              height: sidebar.open ? 'auto' : 0,
            }"
          >
            <div class="truncate route-name flex flex-1 text-body-base font-medium">
              {{ item.name }}
            </div>
            <Motion.div
              v-if="'action' in item && item.action && appStore.isRouteAuthorized(item?.action?.route?.name)"
              :variants="actionVariants"
              class="flex-shrink-0"
            >
              <SidebarNavigationItemAction :action="item.action" />
            </Motion.div>
          </div>
        </Motion.div>
      </component>
    </div>
    <ul
      v-if="item?.children?.length"
      class="list-none m-0 p-0"
      role="list"
    >
      <li
        v-for="child in item.children"
        :key="child.name"
        data-testid="sidebar-nav-child"
        class="my-2xs"
      >
        <SidebarNavigationItem
          v-if="isChildRenderable(child)"
          :item="child"
          :type="type"
        />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  NavigationItem,
  NavigationItemGroupPosition,
} from 'src/core/types/app';
import { useAppStore } from 'src/core/stores/app';
import { useLocalStorage } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { Motion } from 'src/core/composables/useMotion';
import SidebarNavigationItemAction from './SidebarNavigationItemAction.vue';

const props = defineProps<{
  item: NavigationItem;
  type: NavigationItemGroupPosition;
  badge?: string;
}>();

const appStore = useAppStore();
const sidebar = useLocalStorage('sidebar', { open: true });
const { t } = useI18n();

const isRouteItem = computed(() => 'route' in props.item);
const isClickItem = computed(() => 'onClick' in props.item);

const isExplicitlyActive = computed(() => props.item.isActive?.() ?? false);

function isChildRenderable(child: NavigationItem): boolean {
  const authorized =
    'route' in child ? appStore.isRouteAuthorized(child.route.name) : true;
  const visible = child.isVisible?.() ?? true;
  return authorized && visible;
}

const rootTag = computed(() => {
  if (isRouteItem.value) return 'router-link';
  if (isClickItem.value) return 'button';
  return 'a';
});

const rootAttrs = computed(() => {
  if (isRouteItem.value && 'route' in props.item) {
    return { to: props.item.route };
  }
  if ('onClick' in props.item) {
    return {
      type: 'button',
      'aria-label': props.item.name,
      onClick: props.item.onClick,
    };
  }
  if ('link' in props.item) {
    return {
      href: props.item.link,
      target: '_blank',
      rel: 'noopener noreferrer',
      'aria-label': t('app.sidebar.opens_in_new_tab', { name: props.item.name }),
    };
  }
  return {};
});

const textVariants = {
  expanded: {
    width: 'auto',
    opacity: 1,
    marginLeft: 8,
    flex: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  },
  collapsed: {
    width: 0,
    opacity: 0,
    marginLeft: 0,
    flex: 0,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  },
};

const actionVariants = {
  expanded: {
    opacity: 1,
    scale: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  },
  collapsed: {
    opacity: 0,
    scale: 0,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  },
};
</script>

<style lang="scss" scoped>
.sidebar-nav-item.router-link-active,
.sidebar-nav-item.is-active {
  @apply bg-neutral-surface-disabled;

  .route-name {
    @apply text-neutral-text-elevated;
  }

  .meridia-icon {
    @apply text-neutral-text-elevated;
  }

  .component-icon {
    @apply fill-neutral-text-elevated;
  }
}

.sidebar-nav-item:not(.router-link-active):not(.is-active) {
  @apply text-neutral-text-subtitle;
}

.sidebar-nav-item:hover:not(.router-link-active):not(.is-active) {
  @apply bg-neutral-surface-disabled;
}

button.sidebar-nav-item {
  @apply bg-transparent border-0 text-left cursor-pointer;
  font-family: inherit;
  font-size: inherit;
}
</style>
